import React from 'react';

import { TrashCan } from 'src/app/components/icons/trash-can';
import { Button } from 'src/app/components/lib/button';
import { Checkbox } from 'src/app/components/lib/checkbox';
import { Divider } from 'src/app/components/lib/divider';
import { IconButton } from 'src/app/components/lib/icon-button';
import { useDiscountProductsQuery } from 'src/app/queries/discounts/get-discount-products';

import { AppliedFilters } from '../../shared/applied-filters';
import { FilteredProductCount } from '../../shared/filtered-product-count';
import { getRequirementsFromRequirementStepState } from '../../shared/helpers';
import { RequirementCardWrapper, RequirementCardRow, RequirementCardTitle } from '../../shared/styled';
import { useDiscountWizardStateContext } from '../../state';
import { RequirementsStepActions } from '../../state/requirements';

import { ConditionRow } from './condition-row';

type RequirementCardProps = {
  onEditFiltersClick: () => void;
  requirement: number;
};

export function RequirementCard({ onEditFiltersClick, requirement }: RequirementCardProps) {
  const { isLoading: productsLoading } = useDiscountProductsQuery();
  const { isManualDiscount, requirementsStepDispatch, requirementsStepState } = useDiscountWizardStateContext();
  const { requireSameProduct, allowNonCannabisItems } = requirementsStepState[requirement];
  const requirements = getRequirementsFromRequirementStepState(requirementsStepState);
  const requirementCount = Object.keys(requirements).length;

  // at least one requirement is required
  const showDeleteButton = requirementCount > 1;

  return (
    <RequirementCardWrapper>
      <RequirementCardRow $spaceBetween>
        <RequirementCardTitle>Requirement {requirement}</RequirementCardTitle>
        {showDeleteButton && (
          <IconButton
            aria-label={`delete requirement ${requirement}`}
            sx={{ padding: 0 }}
            onClick={() =>
              requirementsStepDispatch({
                type: RequirementsStepActions.DELETE_REQUIREMENT,
                payload: { index: requirement },
              })
            }
          >
            <TrashCan />
          </IconButton>
        )}
      </RequirementCardRow>
      {!isManualDiscount && <ConditionRow requirement={requirement} />}
      <RequirementCardRow $spaceBetween>
        <div>
          Filters applied: <AppliedFilters requirement={requirement} />
        </div>
        <Button
          buttonSize='small'
          disabled={productsLoading}
          label='Edit filters'
          variant='secondary'
          onClick={onEditFiltersClick}
        />
      </RequirementCardRow>
      <RequirementCardRow>
        <div>
          Eligible products: <FilteredProductCount requirement={requirement} />
        </div>
        <Divider variant='vertical-flex' />
        <div>
          <Checkbox
            checked={requireSameProduct}
            label='Require same product'
            labelPlacement='end'
            tooltip='Requires that all items purchased for this constraint are the same product.'
            useV2Tooltip
            onChange={({ target }) =>
              requirementsStepDispatch({
                type: RequirementsStepActions.SET_REQUIRE_SAME_PRODUCT,
                payload: { value: target.checked, index: requirement },
              })
            }
          />
        </div>
        <Divider variant='vertical-flex' />
        <div>
          <Checkbox
            checked={allowNonCannabisItems}
            label='Include non-cannabis items'
            labelPlacement='end'
            onChange={({ target }) =>
              requirementsStepDispatch({
                type: RequirementsStepActions.SET_ALLOW_NON_CANNABIS_ITEMS,
                payload: { value: target.checked, index: requirement },
              })
            }
          />
        </div>
      </RequirementCardRow>
    </RequirementCardWrapper>
  );
}
